import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  TextField,
  Typography,
} from '@mui/material'
import type { FC } from 'react'
import { useState, useEffect } from 'react'

interface ValueEditModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (values: number[]) => void
  title: string
  values: number[]
  isPercentage?: boolean
  decimalPlaces?: number
  min?: number
  max?: number
}

const ValueEditModal: FC<ValueEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  title,
  values,
  isPercentage = true,
  decimalPlaces = 2,
  min = 0,
  max = isPercentage ? 300 : 10,
}) => {
  const [localValues, setLocalValues] = useState<string[]>(['', '', ''])

  useEffect(() => {
    if (isOpen) {
      setLocalValues(
        values.map((value) => (isPercentage ? (value * 100).toFixed(decimalPlaces) : value.toFixed(decimalPlaces)))
      )
    }
  }, [isOpen, values, isPercentage, decimalPlaces])

  const handleValueChange = (index: number, value: string) => {
    const newValues = [...localValues]
    newValues[index] = value
    setLocalValues(newValues)
  }

  const handleSave = () => {
    const numericValues = localValues.map((value) => {
      const num = parseFloat(value) || 0
      return isPercentage ? num / 100 : num
    })

    // Sort values to maintain min <= default <= max
    numericValues.sort((a, b) => a - b)
    onSave(numericValues)
    onClose()
  }

  const handleCancel = () => {
    onClose()
  }

  const isValidValue = (value: string) => {
    const num = parseFloat(value)
    return !isNaN(num) && num >= min && num <= max
  }

  const allValuesValid = localValues.every(isValidValue)

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          Edit {title}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Set the minimum, default, and maximum values. Values will be automatically sorted.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid size={4}>
            <TextField
              fullWidth
              label="Minimum"
              value={localValues[0]}
              onChange={(e) => handleValueChange(0, e.target.value)}
              type="number"
              inputProps={{
                min,
                max,
                step: decimalPlaces === 0 ? 1 : 0.01,
              }}
              InputProps={{
                endAdornment: isPercentage ? '%' : undefined,
                sx: { borderRadius: '21px' },
              }}
              error={!isValidValue(localValues[0])}
              helperText={!isValidValue(localValues[0]) ? `Must be between ${min} and ${max}` : ''}
            />
          </Grid>
          <Grid size={4}>
            <TextField
              fullWidth
              label="Default"
              value={localValues[1]}
              onChange={(e) => handleValueChange(1, e.target.value)}
              type="number"
              inputProps={{
                min,
                max,
                step: decimalPlaces === 0 ? 1 : 0.01,
              }}
              InputProps={{
                endAdornment: isPercentage ? '%' : undefined,
                sx: { borderRadius: '21px' },
              }}
              error={!isValidValue(localValues[1])}
              helperText={!isValidValue(localValues[1]) ? `Must be between ${min} and ${max}` : ''}
            />
          </Grid>
          <Grid size={4}>
            <TextField
              fullWidth
              label="Maximum"
              value={localValues[2]}
              onChange={(e) => handleValueChange(2, e.target.value)}
              type="number"
              inputProps={{
                min,
                max,
                step: decimalPlaces === 0 ? 1 : 0.01,
              }}
              InputProps={{
                endAdornment: isPercentage ? '%' : undefined,
                sx: { borderRadius: '21px' },
              }}
              error={!isValidValue(localValues[2])}
              helperText={!isValidValue(localValues[2]) ? `Must be between ${min} and ${max}` : ''}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="textSecondary">
            <strong>Note:</strong> Values will be automatically sorted after saving to ensure minimum ≤ default ≤
            maximum.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleCancel} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={!allValuesValid}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ValueEditModal
